import { useMemo, useState, useRef } from 'react';
import { useStripe, useElements, IbanElement } from '@stripe/react-stripe-js';
import { Loader2, Lock, AlertTriangle } from 'lucide-react';
import { getSepaErrorMessage } from '../utils/stripeErrors';

interface SepaPaymentFormProps {
  clientSecret: string;
  onSuccess: () => void;
  sellingPrice?: number;
  companyName?: string;
  /** Couleur du bouton de validation (garant) ; couleurs Affinicar par défaut. */
  accentColor?: string;
  accentTextColor?: string;
}

const IBAN_ELEMENT_OPTIONS = {
  supportedCountries: ['SEPA'],
  style: {
    base: {
      fontSize: '16px',
      color: '#333333',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': { color: '#aab7c4' },
      iconColor: '#8074af',
    },
    invalid: {
      color: '#df1b41',
      iconColor: '#df1b41',
    },
  },
};

/**
 * Formulaire SEPA pour PaymentIntent (paiement en ligne unique).
 * Utilise confirmSepaDebitPayment pour traiter le paiement.
 */
export function SepaPaymentForm({
  clientSecret,
  onSuccess,
  sellingPrice,
  companyName,
  accentColor,
  accentTextColor,
}: SepaPaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const clickedOnceRef = useRef(false);
  // Icône du champ IBAN aux couleurs du garant
  const ibanOptions = useMemo(
    () =>
      accentColor
        ? {
            ...IBAN_ELEMENT_OPTIONS,
            style: { ...IBAN_ELEMENT_OPTIONS.style, base: { ...IBAN_ELEMENT_OPTIONS.style.base, iconColor: accentColor } },
          }
        : IBAN_ELEMENT_OPTIONS,
    [accentColor],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || clickedOnceRef.current || !stripe || !elements) return;

    clickedOnceRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(getSepaErrorMessage(submitError));
        clickedOnceRef.current = false;
        setLoading(false);
        return;
      }

      const ibanElement = elements.getElement(IbanElement);
      if (!ibanElement) {
        setError('Veuillez renseigner un IBAN valide.');
        clickedOnceRef.current = false;
        setLoading(false);
        return;
      }

      // @ts-ignore - confirmSepaDebitPayment exists on stripe
      const { error: confirmError } = await stripe.confirmSepaDebitPayment(clientSecret, {
        payment_method: {
          sepa_debit: ibanElement,
          billing_details: { name, email },
        },
      });

      if (confirmError) {
        // Vérifier le statut réel du PaymentIntent
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
        if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
          onSuccess();
          return;
        }
        setError(getSepaErrorMessage(confirmError));
        clickedOnceRef.current = false;
        setLoading(false);
        return;
      }

      onSuccess();
    } catch (err) {
      setError(getSepaErrorMessage(err));
      clickedOnceRef.current = false;
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="tw-space-y-4">
      {/* IBAN */}
      <div>
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
          IBAN
        </label>
        <div className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-3">
          <IbanElement options={ibanOptions} />
        </div>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
          Format : FR76 XXXX XXXX XXXX XXXX XXXX XXX
        </p>
      </div>

      {/* Nom & Email */}
      <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Prénom et Nom
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jean Dupont"
            required
            disabled={loading}
            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm disabled:tw-opacity-50"
          />
        </div>
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@exemple.fr"
            required
            disabled={loading}
            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm disabled:tw-opacity-50"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3 tw-flex tw-items-start tw-gap-2">
          <AlertTriangle className="tw-w-4 tw-h-4 tw-text-red-500 tw-flex-shrink-0 tw-mt-0.5" />
          <p className="tw-text-sm tw-text-red-700 tw-mb-0">{error}</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={!stripe || loading}
        style={accentColor ? { backgroundColor: accentColor, color: accentTextColor ?? '#ffffff' } : undefined}
        className="tw-w-full tw-bg-affinicar-primary tw-text-white tw-rounded-lg tw-px-4 tw-py-3 tw-font-medium tw-text-sm hover:tw-bg-affinicar-secondary hover:tw-brightness-95 tw-transition tw-shadow-sm disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-justify-center tw-gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
            Traitement en cours...
          </>
        ) : (
          'Confirmer le paiement'
        )}
      </button>

      {/* SEPA payment info text */}
      <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-space-y-2">
        {sellingPrice && companyName ? (
          <>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
              En cliquant sur &laquo;&nbsp;Confirmer le paiement&nbsp;&raquo;, vous autorisez
              {' '}{companyName} à débiter <strong>{sellingPrice.toFixed(2)}&euro;</strong> de
              votre compte par prélèvement SEPA.
            </p>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
              Vous bénéficiez d'un droit de remboursement selon les conditions de votre banque.
              La transaction est sécurisée.
            </p>
          </>
        ) : (
          <>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
              En cliquant sur &laquo;&nbsp;Confirmer le paiement&nbsp;&raquo;, vous autorisez
              le débit du montant indiqué par prélèvement SEPA.
            </p>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
              Vous bénéficiez d'un droit de remboursement selon les conditions de votre banque.
              La transaction est sécurisée.
            </p>
          </>
        )}
      </div>

      {/* Security badge */}
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-gray-400">
        <Lock className="tw-w-3 tw-h-3" />
        <span className="tw-text-xs">Paiement sécurisé via Stripe</span>
      </div>
    </form>
  );
}
